import React, {Fragment, useCallback, useEffect, useRef} from 'react';
import ENDPOINT from '../../../constants/endpoints';
import Checkbox from '../../../GbsUI/UI/Checkbox';
import InputLabel from '../../../GbsUI/UI/InputLabel';
import TextInput from '../../../GbsUI/UI/TextInput';
import useApi from '../../../hooks/useApi';
import ResponseErrorDialog from '../../../scenes/Dialog/components/ResponseErrorDialog';

const INTERESTS_OPTIONS = [
  {
    label: 'Private Equity',
    value: 'private_equity',
  },
  {
    label: 'Credit',
    value: 'credit',
  },
  {
    label: 'Corporate Development/M&A',
    value: 'corporate_development',
  },
  {
    label: 'Family Office/Endowments',
    value: 'family_office_endowments',
  },
  {
    label: 'Growth Equity',
    value: 'growth_equity',
  },
  {
    label: 'Venture Capital',
    value: 'venture_capital',
  },
  {
    label: 'Investor Relations',
    value: 'investor_relations',
  },
  {
    label: 'Other (Please Enter)',
    value: '',
  },
  {
    label: 'Unsure',
    value: 'unsure',
  },
];

const INTERESTS_OPTIONS_TWO = [
  {
    label: 'Hedge Fund',
    value: 'hedge_fund'
  },
  {
    label: 'Asset Management',
    value: 'asset_management'
  },
  {
    label: 'Fund of Funds/Secondaries/Co-Invest',
    value: 'fund_of_funds'
  },
  {
    label: 'Corporate Finance/FP&A',
    value: 'corporate_finance_fpa',
  },
  {
    label: 'Search Funds/Startups',
    value: 'search_funds',
  },
  {
    label: 'Business Development',
    value: 'business_development',
  },
  {
    label: 'Advisory/Investment Banking',
    value: 'advisory_investment_banking',
  },
];

const PRIVATE_EQUITY_SUB_OPTIONS = [
  {
    label: 'On-Cycle',
    value: 'private_equity_on_cycle',
  },
  {
    label: 'Off-Cycle',
    value: 'private_equity_off_cycle',
  },
  {
    label: 'Unsure',
    value: 'private_equity_unsure',
  },
];

const RESOURCES_OPTIONS = [
  {
    label: 'Interview Prep Resources',
    value: 'interview_prep_resources'
  },
  {
    label: 'Recruiting Updates',
    value: 'recruiting_updates'
  },
  {
    label: 'Compensation Report / Info',
    value: 'compensation_report_info'
  },
  {
    label: 'Resume help',
    value: 'resume_help'
  },
  {
    label: 'Intros to other recruiters',
    value: 'intros_to_other_recruiters'
  },
];

const RESOURCES_OPTIONS_TWO = [
  {
    label: 'Networking with PE funds in a vertical or city',
    value: 'networking_with_pe_funds'
  },
  {
    label: 'Test Prep / Training Resources',
    value: 'test_prep_training_resources'
  },
  {
    label: 'Jobs opportunities via GoBuyside.com',
    value: 'jobs_opportunities_via_gobuyside_com'
  },
  {
    label: 'Other',
    value: '',
  },
];

const InterestsPage = ({state, updateState, history}) => {
  const api = useApi();
  const formRef = useRef(null);

  const onSubmit = useCallback((e) => {
    e.preventDefault();

    const plans = state.data.careerInterests.filter(item => item !== '');
    const resources = state.data.recruitingResources.filter(item => item !== '');

    if (state.data.careerInterestsCustom) {
      plans.push(state.data.careerInterestsCustom);
    }

    if (state.data.recruitingResourcesCustom) {
      resources.push(state.data.recruitingResourcesCustom);
    }

    if (plans.length === 0) {
      return ResponseErrorDialog({
        response: {
          message: 'Please indicate at least one career interest or select "Unsure"',
        },
      });
    }

    if (resources.length === 0) {
      return ResponseErrorDialog({
        response: {
          message: 'Please select at least one recruiting resource, or select other',
        },
      });
    }

    updateState({
      canContinue: false,
    });

    const data = {
      ...state.data,
      city: state.data.city.value,
      workLocation: state.data.workLocation ? state.data.workLocation.map(item => item.value).join(', ') : '',
      firm: state.data.firm.value || state.data.firm.name,
      school: state.data.school.value || state.data.school.name,
      careerInterests: plans.join(', '),
      recruitingResources: resources.join(', '),
      graduationYear: state.data.graduationYear !== 0 ? state.data.graduationYear : parseInt(state.data.graduationYearCustom, 10),
      firmStartYear: state.data.firmStartYear !== 0 ? state.data.firmStartYear : parseInt(state.data.firmStartYearCustom, 10),
      race: Array.isArray(state.data.race) ? state.data.race.join(', ') : state.data.race,
    };

    api.postRequest(ENDPOINT.STUDENT.SIGN_UP, data).then((response) => {
      if (response.status === 'ERROR') {
        if (response.data.fields.email) {
          ResponseErrorDialog({
            response: {
              message: 'Email already registered',
            },
          });
        }
      } else {
        updateState({
          data: {
            studentId: response.data.id,
          }
        });
        history.replace('/student/success');
      }
    });
  }, [state, api]);

  useEffect(() => {
    updateState({
      canContinue: true,
      onContinue: () => formRef.current.requestSubmit(),
    });
  }, []);

  useEffect(() => {
    if (!state.loading && !state.signUpFilled) {
      history.replace('/student/signup');
    }
  }, [state]);

  return (
    <>
      <h1 className={'student-signup__title student-signup__title--small student-signup__title--left'}>ALMOST DONE...</h1>
      <p className={'student-signup__description student-signup__description--left'}>Describe your recruiting plans and resources of interest</p>
      <div className={'student-signup__content-wrap'}>
        <form ref={formRef} onSubmit={onSubmit} className={'student-form'}>
          <div className={'student-form__row'}>
            <div className={'student-form__col'}>
              <InputLabel
                label={'Career Interests'}
                subLabel={'Which best describes your career interests?'}
                contentClassName={'student-interests'}
              >
                <div className={'student-interests__col'}>
                  {INTERESTS_OPTIONS.map((option) => (
                    <Fragment key={option.value}>
                      <Checkbox
                        label={option.label}
                        checked={state.data.careerInterests.includes(option.value)}
                        onChange={() => {
                          let careerInterests;

                          if (state.data.careerInterests.includes(option.value)) {
                            careerInterests = state.data.careerInterests.filter((i) => {
                              return i !== option.value;
                            });

                            if (option.children) {
                              careerInterests = careerInterests.filter((i) => !option.children.map((c) => c.value).includes(i));
                            }
                          } else {
                            careerInterests = [...state.data.careerInterests, option.value];
                          }

                          updateState({
                            data: {
                              careerInterests,
                              careerInterestsCustom: careerInterests.includes('') ? state.data.careerInterestsCustom : '',
                            }
                          });
                        }}
                      />
                      {option.children && state.data.careerInterests.includes(option.value) && (
                        <div className={'student-sub-input'}>
                          {option.children.map((child) => (
                            <Checkbox
                              label={child.label}
                              checked={state.data.careerInterests.includes(child.value)}
                              onChange={() => {
                                if (state.data.careerInterests.includes(child.value)) {
                                  updateState({data: {careerInterests: state.data.careerInterests.filter((i) => i !== child.value)}});
                                } else {
                                  updateState({data: {careerInterests: [...state.data.careerInterests, child.value]}});
                                }
                              }}
                              key={child.value}
                            />
                          ))}
                        </div>
                      )}
                    </Fragment>
                  ))}
                </div>
                <div className={'student-interests__col'}>
                  {INTERESTS_OPTIONS_TWO.map((option) => (
                    <Checkbox
                      label={option.label}
                      checked={state.data.careerInterests.includes(option.value)}
                      onChange={() => {
                        let careerInterests;

                        if (state.data.careerInterests.includes(option.value)) {
                          careerInterests = state.data.careerInterests.filter((i) => {
                            return i !== option.value;
                          });
                        } else {
                          careerInterests = [...state.data.careerInterests, option.value];
                        }

                        updateState({
                          data: {
                            careerInterests,
                            careerInterestsCustom: careerInterests.includes('') ? state.data.careerInterestsCustom : '',
                          }
                        });
                      }}
                      key={option.value}
                    />
                  ))}
                </div>
              </InputLabel>
            </div>
          </div>
          {state.data.careerInterests && state.data.careerInterests.includes('private_equity') && (
            <div className={'student-form__row'}>
              <div className={'student-form__col'}>
                <InputLabel
                  label={'Describe your Private Equity recruiting plans (optional):'}
                  contentClassName={'student-interests'}
                >
                  <div className={'student-interests__col'}>
                    {PRIVATE_EQUITY_SUB_OPTIONS.map((option) => (
                      <Fragment key={option.value}>
                        <Checkbox
                          label={option.label}
                          checked={state.data.careerInterests.includes(option.value)}
                          onChange={() => {
                            let careerInterests;

                            if (state.data.careerInterests.includes(option.value)) {
                              careerInterests = state.data.careerInterests.filter((i) => {
                                return i !== option.value;
                              });
                            } else {
                              careerInterests = [...state.data.careerInterests, option.value];
                            }

                            updateState({
                              data: {
                                careerInterests,
                              }
                            });
                          }}
                        />
                      </Fragment>
                    ))}
                  </div>
                </InputLabel>
              </div>
            </div>
          )}
          {state.data.careerInterests && state.data.careerInterests.includes('') && (
            <TextInput
              value={state.data.careerInterestsCustom}
              onChange={(e) => updateState({data: {careerInterestsCustom: e.target.value}})}
              label={'Other: Please describe'}
              placeholder={''}
            />
          )}
          <div className={'student-form__row'}>
            <div className={'student-form__col'}>
              <InputLabel
                label={'Recruiting resources'}
                subLabel={'As they\'re available, which recruiting resources would you like us to share?'}
                contentClassName={'student-interests'}
              >
                <div className={'student-interests__col'}>
                  {RESOURCES_OPTIONS.map((option) => (
                    <Checkbox
                      label={option.label}
                      checked={state.data.recruitingResources.includes(option.value)}
                      onChange={() => {
                        let recruitingResources;

                        if (state.data.recruitingResources.includes(option.value)) {
                          recruitingResources = state.data.recruitingResources.filter((i) => {
                            return i !== option.value;
                          });
                        } else {
                          recruitingResources = [...state.data.recruitingResources, option.value];
                        }

                        updateState({
                          data: {
                            recruitingResources,
                            recruitingResourcesCustom: recruitingResources.includes('') ? state.data.recruitingResourcesCustom : '',
                          }
                        });
                      }}
                      key={option.value}
                    />
                  ))}
                </div>
                <div className={'student-interests__col'}>
                  {RESOURCES_OPTIONS_TWO.map((option) => (
                    <Checkbox
                      label={option.label}
                      checked={state.data.recruitingResources.includes(option.value)}
                      onChange={() => {
                        let recruitingResources;

                        if (state.data.recruitingResources.includes(option.value)) {
                          recruitingResources = state.data.recruitingResources.filter((i) => {
                            return i !== option.value;
                          });
                        } else {
                          recruitingResources = [...state.data.recruitingResources, option.value];
                        }

                        updateState({
                          data: {
                            recruitingResources,
                            recruitingResourcesCustom: recruitingResources.includes('') ? state.data.recruitingResourcesCustom : '',
                          }
                        });
                      }}
                      key={option.value}
                    />
                  ))}
                </div>
              </InputLabel>
            </div>
          </div>
          {state.data.recruitingResources && state.data.recruitingResources.includes('') && (
            <TextInput
              value={state.data.recruitingResourcesCustom}
              onChange={(e) => updateState({data: {recruitingResourcesCustom: e.target.value}})}
              label={'Other: Please describe'}
              placeholder={''}
            />
          )}
          <TextInput
            value={state.data.notes}
            onChange={(e) => updateState({data: {notes: e.target.value}})}
            label={'Other comments/questions?'}
            placeholder={''}
            multiline
            rows={4}
          />
        </form>
      </div>
    </>
  );
};

export default InterestsPage;
